import { useState, useEffect } from "react";
import { isOmniHost, resolveTenantForHost, applyBranding, type TenantInfo } from "../lib/tenant";
import { Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Lock,
  ArrowRight,
  ShieldCheck,
  TrendingUp,
  BrainCircuit,
} from "lucide-react";
import { useAuthStore } from "../store/useAuthStore";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const { login, verifyTwoFactor, isLoggingIn } = useAuthStore();

  // Omni Workspace hosts: the hostname sets the initial tenant context.
  const [omniCtx, setOmniCtx] = useState<{ central: boolean; tenant: TenantInfo | null } | null>(null);
  useEffect(() => {
    if (isOmniHost())
      resolveTenantForHost().then((r) => {
        setOmniCtx(r);
        if (r.tenant) applyBranding(r.tenant); // branded authentication experience
      });
  }, []);
  const brandTitle = !isOmniHost()
    ? "CRE Ecosystem"
    : omniCtx?.tenant?.name || (omniCtx && !omniCtx.central && !omniCtx.tenant ? "Unknown Workspace" : "Omni Workspace");
  const brandSub = !isOmniHost()
    ? "Sign in to your client portal to manage projects."
    : omniCtx && !omniCtx.central && !omniCtx.tenant
    ? "This workspace does not exist or is inactive."
    : omniCtx?.central
    ? "Sign in to your workspace." // central portal — team members and clients alike
    : "Sign in to your client workspace.";

  // Two-factor challenge state.
  const [twoFa, setTwoFa] = useState<{ userId: string } | null>(null);
  const [code, setCode] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await login(formData);
    if (res?.twoFactorRequired && res.userId) setTwoFa({ userId: res.userId });
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await verifyTwoFactor({ userId: twoFa!.userId, code });
  };

  return (
    <div className="bd-workspace min-h-screen grid place-items-center bg-[#eaeef7] p-4 sm:p-8">
      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-[0_25px_70px_-20px_rgba(15,30,70,0.28)] flex overflow-hidden">

      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-14 py-14 min-h-[600px]">

        <div className="max-w-sm w-full mx-auto">
          {omniCtx?.tenant?.logoUrl && (
            <img src={omniCtx.tenant.logoUrl} alt={brandTitle} className="h-10 mb-4 object-contain self-start" />
          )}
          <h1 className="text-3xl sm:text-[34px] font-extrabold text-[color:var(--brand-hex,#0058c3)] tracking-tight leading-[1.1]">
            {brandTitle}
          </h1>
          <p className="text-[15px] text-slate-500 mt-2.5 leading-relaxed">
            {brandSub}
          </p>

          {twoFa ? (
            <form onSubmit={handleVerify} className="mt-8 space-y-4">
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 flex items-start gap-2.5">
                <ShieldCheck size={18} className="text-[#0058c3] mt-0.5 shrink-0" />
                <p className="text-[13px] text-slate-600">We emailed you a 6-digit verification code. Enter it below to finish signing in.</p>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1.5 uppercase tracking-wider">Verification code</label>
                <input
                  autoFocus inputMode="numeric" maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="w-full px-4 py-3 text-center text-lg tracking-[0.4em] font-bold border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0070f3]/30"
                />
              </div>
              <button type="submit" disabled={isLoggingIn || code.length < 6}
                className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-hex,#0058c3)] text-white font-semibold py-3 rounded-xl hover:opacity-90 disabled:opacity-50">
                {isLoggingIn ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Verify &amp; sign in
              </button>
              <button type="button" onClick={() => { setTwoFa(null); setCode(""); }}
                className="w-full text-[13px] font-semibold text-slate-500 hover:text-slate-700">
                Back to sign in
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">

            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                Email or Username
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  required
                  autoComplete="username"
                  className="w-full pl-10 pr-4 py-3 text-sm border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0070f3]/30 focus:border-[#0070f3]/50 transition placeholder:text-slate-400"
                  placeholder="you@example.com or your username"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  Password
                </label>
                <Link
                  to="#"
                  className="text-[11px] font-semibold text-[#0058c3] hover:text-[#0070f3] transition"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-3 text-sm border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0070f3]/30 focus:border-[#0070f3]/50 transition placeholder:text-slate-400"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-[#0058c3] focus:ring-2 focus:ring-[#0070f3]/30 cursor-pointer"
              />
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition">
                Remember me for 30 days
              </span>
            </label>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full mt-2 py-3 rounded-xl bg-[color:var(--brand-hex,#0058c3)] hover:opacity-90 text-white font-semibold text-sm shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Signing in…
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>
          )}

          <div className="my-6 h-px bg-slate-200" />

          <p className="text-center text-sm text-slate-600">
            Don't have an account?{" "}
            <span className="font-semibold text-[color:var(--brand-hex,#0058c3)]">Contact your administrator</span>
          </p>
        </div>
      </div>

      <div
        className="hidden lg:flex relative overflow-hidden text-white rounded-2xl m-3 w-1/2"
        style={{ background: "radial-gradient(ellipse at top right, var(--brand-panel-light, #0a3470) 0%, var(--brand-panel-mid, #06255a) 55%, var(--brand-panel-dark, #001a43) 100%)" }}
      >

        <div
          aria-hidden
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(700px 400px at 80% 0%, color-mix(in srgb, var(--brand-hex, #0070f3) 20%, transparent), transparent 65%)",
          }}
        />

        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at center, white 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="relative flex flex-col justify-center p-10 xl:p-12 w-full">

          <div>
            <span className="inline-block text-[11px] font-bold text-white uppercase tracking-[0.15em] bg-[color:var(--brand-hex,#0058c3)] px-3 py-1.5 rounded-md mb-6">
              Enterprise Precision
            </span>
            <h2 className="text-3xl xl:text-[40px] font-extrabold tracking-tight leading-[1.1] text-white">
              Elevate your workspace efficiency
            </h2>
            <p className="text-[15px] text-slate-300/80 mt-4 max-w-md leading-relaxed">
              Streamline your operations with our integrated suite of project management and document security tools.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            <FeatureCard icon={TrendingUp} title="Real-time Project Tracking"
              body="Monitor milestones and resource allocation with live visual dashboards." />
            <FeatureCard icon={ShieldCheck} title="Secure Documents"
              body="Bank-grade encryption for all your sensitive project assets and contracts." />
            <FeatureCard icon={BrainCircuit} title="Direct AI Insights"
              body="Leverage predictive analytics to forecast bottlenecks and budget risks." />
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between">
            <div className="flex items-center">
              <span className="w-7 h-7 rounded-full bg-slate-300 ring-2 ring-[color:var(--brand-panel-mid,#06255a)]" />
              <span className="w-7 h-7 rounded-full bg-slate-400 ring-2 ring-[color:var(--brand-panel-mid,#06255a)] -ml-2" />
              <span className="w-7 h-7 rounded-full bg-white text-[color:var(--brand-panel-mid,#06255a)] text-[9px] font-bold grid place-items-center ring-2 ring-[color:var(--brand-panel-mid,#06255a)] -ml-2">+12k</span>
            </div>
            <p className="text-[12px] font-semibold text-slate-400">Trusted by world-leading enterprises.</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, body }: { icon: any; title: string; body: string }) => (
  <div className="flex items-start gap-3.5 rounded-xl border border-white/10 bg-white/[0.04] p-4">
    <div className="w-10 h-10 rounded-lg bg-[color:var(--brand-hex,#0058c3)] grid place-items-center shrink-0">
      <Icon size={18} className="text-white" />
    </div>
    <div>
      <p className="text-[15px] font-bold text-white leading-tight">{title}</p>
      <p className="text-[13px] text-slate-300/70 mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

export default LoginPage;
